import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { ShippingMode } from '@/types/shipping';
import Button from '@appchoose/button';
import { AlertCircleIcon, CheckIcon, SettingsIcon } from 'lucide-react';

import FreeShippingBlock from './free-shipping-block';
import { PickupPointBlock } from './pickup-point-block';
import { ShippingPolicyDialogLocalManager } from './shipping-policy-dialog-localmanager';

interface ShippingPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  onConfirm?: (open: boolean) => void;
  mode?: ShippingMode;
}

function ShippingPolicyDialog({
  open,
  onOpenChange,
  dealId,
  onConfirm,
  mode = ShippingMode.Validate,
}: ShippingPolicyDialogProps) {
  const { t } = useTranslation();
  const isOfferLocked = useIsOfferLocked();
  const handleClose = () => {
    onOpenChange(false);
  };

  const handleDontShow = () => {
    ShippingPolicyDialogLocalManager.setDontShowAgain(true);
    handleConfirm();
  };

  const handleConfirm = () => {
    handleClose();
    onConfirm?.(true);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {mode === ShippingMode.EditBulk
              ? t('ui-v2.settings.blocks-shipping-policy.shipping-settings')
              : t('ui-v2.settings.blocks-shipping-policy.before-validate')}
          </DialogTitle>
          <DialogDescription>
            {mode === ShippingMode.EditBulk
              ? t('ui-v2.settings.blocks-shipping-policy.you-want')
              : t(
                  'ui-v2.settings.blocks-shipping-policy.free-shipping.no-shipping-offer'
                ) +
                '. ' +
                t('ui-v2.settings.blocks-shipping-policy.you-want')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FreeShippingBlock dealId={dealId} disabled={true} />
        </div>
        <div className="space-y-4">
          <PickupPointBlock dealId={dealId} disabled={true} />
        </div>
        {isOfferLocked && (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            <AlertCircleIcon className="h-6 w-6" />
            {t(
              'ui-v2.settings.blocks-shipping-policy.free-shipping.is-offer-locked'
            )}
          </div>
        )}
        <DialogFooter>
          {mode === ShippingMode.EditBulk && (
            <Button type="button" appearance="outline" onClick={handleDontShow}>
              {t('general.dont_show_again')}
            </Button>
          )}

          <Link to={`/deal/${dealId}/settings`} onClick={handleClose}>
            <Button
              type="button"
              appearance={
                mode === ShippingMode.EditBulk ? 'primary' : 'outline'
              }
            >
              <SettingsIcon className="mr-2 h-4 w-4" />
              {t('general.configure')}
            </Button>
          </Link>
          {mode === ShippingMode.Validate && (
            <Button type="button" appearance="primary" onClick={handleConfirm}>
              <CheckIcon className="mr-2 h-4 w-4" />
              {t('general.validate')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShippingPolicyDialog;
